/**
 * Copyright (C) 2021 Tencent.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import ParagraphBase from '@/core/ParagraphBase';
import { getTableRule } from '@/utils/regexp';
import Logger from '@/Logger';

const TABLE_LOOSE = 'loose';
const TABLE_STRICT = 'strict';

export default class Table extends ParagraphBase {
  static HOOK_NAME = 'table';

  constructor({ externals, config, cherry }) {
    super({ needCache: true });
    const {
      enableChart,
      selfClosing,
      chartRenderEngine: ChartRenderEngine,
      externals: requiredPackages,
      chartEngineOptions = {},
    } = config;
    this.chartRenderEngine = null;
    this.selfClosing = selfClosing;
    if (enableChart === true) {
      try {
        this.chartRenderEngine = new ChartRenderEngine({
          // 注入需要的第三方包
          ...(externals &&
            requiredPackages instanceof Array &&
            requiredPackages.reduce((acc, pkg) => {
              delete chartEngineOptions[pkg]; // 过滤第三方包选项
              return { ...acc, [pkg]: externals[pkg] };
            }, {})),
          renderer: 'svg',
          width: 500,
          height: 300,
          ...chartEngineOptions,
          // 传递Cherry配置以供地图数据源使用
          cherryOptions: cherry ? cherry.options : null,
          // 传递Cherry实例以供事件监听使用
          cherry,
        });
      } catch (error) {
        console.warn(error);
      }
    }
  }

  // 保持每列长度一致
  $extendColumns(row, colCount) {
    const delta = colCount - row.length;
    if (delta < 1) {
      return row;
    }
    return row.concat('&nbsp;|'.repeat(delta).split('|', delta));
  }

  $parseChartOptions(cell) {
    // Logger.log('Parsing chart options for cell:', cell);
    // 初始化失败
    if (!this.chartRenderEngine) {
      // Logger.log('Chart render engine not available');
      return null;
    }
    const CHART_REGEX = /^:(\w+):(?:[ ]*{(.*?)}[ ]*)?$/;
    if (!CHART_REGEX.test(cell)) {
      // Logger.log('Cell does not match chart regex:', cell);
      return null;
    }
    const match = cell.match(CHART_REGEX);
    const [, chartType, options] = match;
    const result = {
      type: chartType,
      options: options ? this.$parseProps(options) : {},
    };
    // Logger.log('Parsed chart options:', result);
    return result;
  }

  $parseProps(str) {
    if (!str || !str.trim()) {
      return {};
    }
    const trimmedStr = str.trim();
    // 使用更强大的JSON格式，为实现渐进式迁移，通过启发式判断来选择解析方式。
    // 如果内容以双引号开头，则认为用户希望编写JSON。
    if (trimmedStr.startsWith('"')) {
      const jsonString = `{${trimmedStr}}`;
      try {
        return JSON.parse(jsonString, (key, value) => {
          // 安全检查：在解析过程中，如果遇到恶意键，则直接忽略它们。
          if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            return undefined;
          }
          return value;
        });
      } catch (error) {
        Logger.error(
          'Invalid JSON format in chart options. Please check your syntax. Common errors include missing quotes on keys, using single quotes for strings, or a trailing comma.\n' +
            `Error: ${error.message}\n` +
            'Attempted to parse:',
          jsonString,
        );
        return {};
      }
    } else {
      // 内容不以双引号开头，则假定用户使用旧格式。
      // 使用旧的解析器进行解析，以保证向后兼容。
      // 手动解析 "key: value, other: 123" 这样的字符串
      const result = {};
      // 匹配 key: value 对，支持无引号、有引号、数字等
      const pairs = trimmedStr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const kvRegex = /^(\w+|\s*".*?"\s*|\s*'.*?'\s*)\s*:\s*(.+?)$/;
      for (const pair of pairs) {
        const kvMatch = pair.match(kvRegex);
        if (kvMatch) {
          const k = kvMatch[1].trim().replace(/^["']|["']$/g, ''); // 去除引号
          let v = kvMatch[2].trim();
          // 尝试转换数字
          if (/^-?\d+(\.\d+)?$/.test(v)) v = Number(v);
          else if (v === 'true') v = true;
          else if (v === 'false') v = false;
          else v = v.replace(/^["']|["']$/g, ''); // 值也去引号
          result[k] = v;
        }
      }

      // 只要旧解析器成功解析出任何内容，就打印弃用警告。
      if (Object.keys(result).length > 0) {
        Logger.warn(
          'DEPRECATION WARNING: The chart options syntax is outdated and will be removed in a future version. Please switch to standard JSON format.\n' +
            `e.g., Change '{ title: My Chart }' to '{ "title": "My Chart" }'.\n` +
            'Offending string:',
          str,
        );
      }

      return result;
    }
  }

  $parseColumnAlignRules(row) {
    const COLUMN_ALIGN_MAP = { L: 'left', R: 'right', C: 'center' };
    const COLUMN_ALIGN_CACHE_SIGN = ['U', 'L', 'R', 'C']; // U for undefined
    const textAlignRules = row.map(($rule) => {
      let index = 0;
      if (/^:/.test($rule)) {
        index += 1;
      }
      if (/:$/.test($rule)) {
        index += 2;
      }
      return COLUMN_ALIGN_CACHE_SIGN[index];
    });
    return { textAlignRules, COLUMN_ALIGN_MAP };
  }

  $parseTable(lines, sentenceMakeFunc, dataLines, originalStr = '') {
    let maxCol = 0;
    const rows = lines.map((line, index) => {
      const cols = line.replace(/\\\|/g, '~CS').split('|');
      if (cols[0] === '') {
        cols.shift();
      }
      if (cols[cols.length - 1] === '') {
        cols.pop();
      }
      // 文本对齐相关列，不作为最多列数的参考依据
      index !== 1 && (maxCol = Math.max(maxCol, cols.length));
      return cols.map((col) => col.trim());
    });
    const { textAlignRules, COLUMN_ALIGN_MAP } = this.$parseColumnAlignRules(rows[1]);
    const tableObject = {
      header: [],
      rows: [],
      colLength: maxCol,
      rowLength: rows.length - 2, // 去除表头和控制行
    };
    const chartOptions = this.$parseChartOptions(rows[0][0]);
    const chartOptionsSign = this.$engine.hash(rows[0][0]);
    // 如果需要生成图表，
    if (chartOptions) {
      rows[0][0] = '';
    }
    // 在流式输出场景下，光标占位符 CHERRYFLOWSESSIONCURSOR 可能被混入到表格单元格文本中，
    // 一旦这些文本被图表插件序列化进 data-table-data / data-chart-options 的属性值 JSON 中，
    // 最终 $clearFlowSessionCursorCache 会把 <span class="cherry-flow-session-cursor"></span>
    // 这样带双引号的片段替换进属性值内部，导致浏览器解析 HTML 时属性被提前闭合，
    // 进而出现属性值后半段泄漏为文本节点的问题（#cherry-echarts-wrapper 内容泄漏 bug）。
    // 这里仅在需要生成图表时清理单元格中的占位符，避免影响普通表格的光标显示。
    const stripFlowCursor = (cell) => (typeof cell === 'string' ? cell.replace(/CHERRYFLOWSESSIONCURSOR/g, '') : cell);
    /**
     * ~CTHD: <thead>
     * ~CTHD$: </thead>
     * ~CTBD: <tbody>
     * ~CTBD$: </tbody>
     * ~CTR: <tr>
     * ~CTR$: </tr>
     * ~CTH(L|R|C|U): <th>
     * ~CTH$: </th>
     * ~CTD(L|R|C|U): <td>
     * ~CTD$: </td>
     */
    const tableHeader = this.$extendColumns(rows[0], maxCol)
      .map((cell, col) => {
        tableObject.header.push(
          chartOptions ? stripFlowCursor(cell.replace(/~CS/g, '\\|')) : cell.replace(/~CS/g, '\\|'),
        );
        const { html: cellHtml } = sentenceMakeFunc(cell.replace(/~CS/g, '\\|'));
        // 前后补一个空格，否则自动链接会将缓存的内容全部收入链接内部
        return `~CTH${textAlignRules[col] || 'U'} ${cellHtml} ~CTH$`;
      })
      .join('');
    const tableRows = rows
      .reduce((table, row, line) => {
        if (line <= 1) {
          return table;
        }
        const currentRowCountWithoutHeader = line - 2;
        tableObject.rows[currentRowCountWithoutHeader] = [];
        const $extendedColumns = this.$extendColumns(row, maxCol).map((cell, col) => {
          tableObject.rows[currentRowCountWithoutHeader].push(
            chartOptions ? stripFlowCursor(cell.replace(/~CS/g, '\\|')) : cell.replace(/~CS/g, '\\|'),
          );
          const { html: cellHtml } = sentenceMakeFunc(cell.replace(/~CS/g, '\\|'));
          // 前后补一个空格，否则自动链接会将缓存的内容全部收入链接内部
          return `~CTD${textAlignRules[col] || 'U'} ${cellHtml} ~CTD$`;
        });
        table.push(`~CTR${$extendedColumns.join('')}~CTR$`);
        return table;
      }, [])
      .join('');
    // console.log('obj', tableObject);
    const tableResult = this.$renderTable(COLUMN_ALIGN_MAP, tableHeader, tableRows, dataLines);
    if (!chartOptions) {
      return tableResult;
    }
    // note: 下面的代码会导致局部的地图数据源配置影响全局的配置，导致所有地图显示相同的界面
    // 检查是否有地图数据源注释（针对地图类型的图表）
    // let enhancedChartOptions = chartOptions;
    // if (chartOptions.type === 'map') {
    //   // 在前面的内容中查找 mapDataSource 注释 - 支持更灵活的格式
    //   const mapDataSourceRegex = /<!--[\s\S]*?mapDataSource:\s*([^\s>]+)[\s\S]*?-->/i;
    //   const match = originalStr.match(mapDataSourceRegex);
    //   if (match && match[1]) {
    //     const mapDataSource = match[1].trim();
    //     Logger.log('从注释中检测到地图数据源:', mapDataSource);
    //     enhancedChartOptions = {
    //       ...chartOptions,
    //       options: {
    //         ...chartOptions.options,
    //         mapDataSource,
    //       },
    //     };
    //   } else {
    //     // 如果没有检测到自定义数据源，记录日志便于调试
    //     Logger.log('未检测到自定义地图数据源，使用默认配置');
    //     Logger.log('originalStr preview:', originalStr.substring(0, 200));
    //   }
    // }
    const chart = this.chartRenderEngine.render(
      chartOptions.type,
      chartOptions.options,
      tableObject,
      this.$engine.$cherry,
    );
    const chartHtml = `<figure class="cherry-table-figure">${chart}</figure>`;
    const newSign = `${tableResult.sign}${chartOptionsSign}`;
    return {
      html: tableResult.html
        .replace(/(^<div .*?>)/, `$1${chartHtml}`)
        .replace(/(^<div .*? data-sign=")[^"]+?"/, `$1${newSign}"`),
      sign: newSign,
    };
  }

  /**
   * 如果table.head是空的，就不渲染<thead>了
   * @param {String} str
   * @returns {Boolean}
   */
  $testHeadEmpty(str) {
    const test = str
      .replace(/&nbsp;/g, '')
      .replace(/\s/g, '')
      .replace(/(~CTH\$|~CTHU|~CTHL|~CTHR|~CTHC)/g, '');
    return test?.length > 0;
  }

  $renderTable(COLUMN_ALIGN_MAP, tableHeader, tableRows, dataLines) {
    const cacheSrc = this.$testHeadEmpty(tableHeader)
      ? `~CTHD${tableHeader}~CTHD$~CTBD${tableRows}~CTBD$`
      : `~CTBD${tableRows}~CTBD$`;
    const html = cacheSrc;
    const sign = this.$engine.hash(html);
    const renderHtml = html
      .replace(/~CTHD\$/g, '</thead>')
      .replace(/~CTHD/g, '<thead>')
      .replace(/~CTBD\$/g, '</tbody>')
      .replace(/~CTBD/g, '</tbody>')
      .replace(/~CTR\$/g, '</tr>')
      .replace(/~CTR/g, '<tr>')
      .replace(/[ ]?~CTH\$/g, '</th>')
      .replace(/[ ]?~CTD\$/g, '</td>') // 在这里将加上的空格还原回来
      .replace(/~CT(D|H)(L|R|C|U)[ ]?/g, (match, type, align) => {
        let tag = `<t${type}`;
        if (align === 'U') {
          tag += '>';
        } else {
          tag += ` align="${COLUMN_ALIGN_MAP[align]}">`;
        }
        return tag;
      })
      .replace(/\\\|/g, '|'); // escape \|
    return {
      html: `<div class="cherry-table-wrapper" data-sign="${sign}${dataLines}" data-lines="${dataLines}">
        <div class="cherry-table-container"><table class="cherry-table">${renderHtml}</table></div></div>`,
      sign,
    };
  }

  makeHtml(str, sentenceMakeFunc) {
    let $str = str;
    // 表格在流式输出下的适配
    if (this.$engine.$cherry.options.engine.global.flowSessionContext || this.selfClosing) {
      if (/(^|^(\s*[^|\s])[^\n]*\n|\n\n|\n(\s*[^|\s])[^\n]*\n)\s*\|[^\n]+\n{0,1}[|:-\s]*\n*$/.test($str)) {
        $str = `${$str.replace(/\n[|:-\s]*\n*$/, '')}\n|-|`;
      }
    }
    // strict fenced mode
    if (this.test($str, TABLE_STRICT)) {
      $str = $str.replace(this.RULE[TABLE_STRICT].reg, (match, leading) => {
        const dataLines = this.getLineCount(match, leading);
        // 必须先trim，否则分割出来的结果不对
        // 将fenced mode转换为loose mode
        const lines = match
          .trim()
          .split(/\n/)
          .map((line) => String(line).trim());
        const { html: table, sign } = this.$parseTable(lines, sentenceMakeFunc, dataLines, $str);
        return this.getCacheWithSpace(this.pushCache(table, sign, dataLines), match);
      });
    }
    // loose mode
    if (this.test($str, TABLE_LOOSE)) {
      // console.log(TABLE_LOOSE);
      $str = $str.replace(this.RULE[TABLE_LOOSE].reg, (match, leading) => {
        const dataLines = this.getLineCount(match, leading);
        // 必须先trim，否则分割出来的结果不对
        const lines = match
          .trim()
          .split(/\n/)
          .map((line) => String(line).trim());
        const { html: table, sign } = this.$parseTable(lines, sentenceMakeFunc, dataLines, $str);
        return this.getCacheWithSpace(this.pushCache(table, sign, dataLines), match);
      });
    }
    return $str;
  }

  test(str, flavor) {
    return this.RULE[flavor].reg && this.RULE[flavor].reg.test(str);
  }

  /**
   * TODO: fix type errors
   * @returns
   */
  rule() {
    return /** @type {any} */ (getTableRule());
  }
}
